import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import request from "@/services/request";
import router from "@/router";
import EventBus from "@/services/eventBus";
import StorageManager from "@/services/storageManager";

export default class PushManager
{
    static initialized = false;

    /**
     * Initialize push notifications after successful login.
     * Idempotent — skips if already initialized or not on a native platform.
     */
    static async initialize()
    {
        if (this.initialized || !Capacitor.isNativePlatform()) {
            return;
        }

        this.registerListeners();
        await this.requestPermission();
    }

    /**
     * Register all push notification event listeners.
     */
    static registerListeners()
    {
        PushNotifications.addListener('registration', (token) => {
            this.onRegistration(token.value);
        });

        PushNotifications.addListener('registrationError', (error) => {
            console.error('Push registration failed:', error);
        });

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
            this.onForegroundNotification(notification);
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            this.onNotificationTap(action.notification);
        });

        this.initialized = true;
    }

    /**
     * Request permission and register with native push service.
     */
    static async requestPermission()
    {
        let permission = await PushNotifications.checkPermissions();

        if (permission.receive === 'prompt') {
            permission = await PushNotifications.requestPermissions();
        }

        if (permission.receive === 'granted') {
            await PushNotifications.register();
        }
    }

    /**
     * Handle successful registration — send token to the API.
     */
    static async onRegistration(token)
    {
        const currentToken = StorageManager.get('push_token');

        // Skip API call if token hasn't changed
        if (currentToken === token) {
            return;
        }

        const identifier = StorageManager.get('identifier');
        if (!identifier) {
            return;
        }

        try {
            await request('device/push-token', {
                method: 'post',
                data: {
                    identifier,
                    push_token: token,
                },
            });
            StorageManager.set('push_token', token);
        } catch (error) {
            console.error('Failed to register push token:', error);
        }
    }

    /**
     * Handle a notification received while the app is in the foreground.
     * Emits an event for the UI to display as an in-app toast.
     */
    static onForegroundNotification(notification)
    {
        EventBus.emit('push-notification', {
            title: notification.title,
            body: notification.body,
            data: notification.data,
        });
    }

    /**
     * Handle notification tap — navigate to the appropriate deep link.
     */
    static onNotificationTap(notification)
    {
        const deepLink = notification.data?.deep_link;

        if (!deepLink) {
            return;
        }

        const loggedIn = StorageManager.has('identifier');

        if (!loggedIn) {
            StorageManager.set('push_deep_link', deepLink);
            return;
        }

        this.navigateToDeepLink(deepLink);
    }

    /**
     * Navigate to a deep link path using the Vue router.
     * Falls back to the notifications page if the deep link cannot be resolved.
     */
    static navigateToDeepLink(deepLink)
    {
        const resolved = router.resolve(deepLink);

        if (!resolved || resolved.matched.length === 0) {
            this.navigateToNotifications();
            return;
        }

        router.push(deepLink).catch(() => {
            this.navigateToNotifications();
        });
    }

    /**
     * Fallback navigation to the notifications page.
     */
    static navigateToNotifications()
    {
        router.push({name: 'notifications'}).catch(() => {});
    }

    /**
     * Check for and navigate to a stored deep link (called after login completes).
     */
    static consumePendingDeepLink()
    {
        const pending = StorageManager.get('push_deep_link');

        if (pending) {
            StorageManager.delete('push_deep_link');
            this.navigateToDeepLink(pending);
        }
    }

    /**
     * Remove the push token from the API and clear local storage.
     * Called on logout.
     */
    static async unregister()
    {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        const identifier = StorageManager.get('identifier');
        const token = StorageManager.get('push_token');

        if (identifier && token) {
            try {
                await request('device/push-token', {
                    method: 'delete',
                    data: {
                        identifier,
                        push_token: token,
                    },
                });
            } catch {
                // Silent failure on logout — token will expire naturally
            }
        }

        StorageManager.delete('push_token');
        this.initialized = false;
        await PushNotifications.removeAllListeners();
    }
}
